import { Component, inject, computed, signal, effect } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { UploadProgressService, UploadTask } from '../../_services/upload-progress.service';
import {
  fadeInUpOnEnterAnimation,
  fadeOutDownOnLeaveAnimation,
  fadeInOnEnterAnimation,
  fadeOutOnLeaveAnimation,
} from '../../_animations';

@Component({
  selector: 'app-upload-progress-popup',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatProgressBarModule],
  template: `
    @if (hasActiveUploads()) {
      <div
        class="fixed bottom-5 right-5 z-[1000] max-h-96 w-80 rounded-lg bg-white p-4 shadow-xl"
        [@fadeInUpOnEnter]
        [@fadeOutDownOnLeave]
      >
        <div class="overflow-hidden rounded-lg">
          <div class="flex flex-row justify-between">
            <div class="my-auto flex gap-2 text-sm font-medium">
              <mat-icon class="scale-110">cloud_upload</mat-icon>
              <span class="text-sm">Uploading {{ tasks().length }} item(s)</span>
            </div>
            <button mat-icon-button class="ml-auto" (click)="toggleMinimize()">
              <mat-icon
                class="transition-transform duration-200 ease-in-out"
                [class]="isMinimized() ? 'rotate-180' : ''"
              >
                expand_more
              </mat-icon>
            </button>
          </div>
          @if (!isMinimized()) {
            <div [@fadeInOnEnter] [@fadeOutOnLeave] class="pt-2">
              <div class="max-h-72 overflow-y-auto">
                @for (task of tasks(); track trackByTaskId($index, task)) {
                  <div class="border-b border-gray-100 px-2 py-3 transition-colors last:border-b-0">
                    <div class="mb-2 flex items-center gap-3">
                      @if (task.thumbnailUrl) {
                        <div class="h-10 w-10 flex-shrink-0 overflow-hidden rounded">
                          <img [src]="task.thumbnailUrl" alt="Thumbnail" class="h-full w-full object-cover" />
                        </div>
                      }
                      <div class="min-w-0 flex-1">
                        <div class="overflow-hidden text-ellipsis whitespace-nowrap text-xs font-medium">
                          {{ task.title }}
                        </div>
                        <div class="mt-0.5 text-xs text-gray-600">
                          {{ task.message }}
                        </div>
                      </div>
                    </div>
                    <div class="flex flex-row gap-1">
                      @if (task.status === 'completed') {
                        <mat-progress-bar
                          [value]="100"
                          mode="determinate"
                          class="!my-auto h-1 rounded-sm [--mat-progress-bar-active-indicator-color:theme(colors.emerald.500)]"
                        ></mat-progress-bar>
                        @if (task.status === 'completed') {
                          <mat-icon class="h-4 w-4 text-base text-emerald-500"> check_circle </mat-icon>
                        }
                      } @else if (task.status === 'uploading' || task.status === 'processing') {
                        <mat-progress-bar
                          [value]="task.progress.overall"
                          mode="buffer"
                          class="!my-auto h-1 rounded-sm"
                        ></mat-progress-bar>
                      } @else {
                        <mat-progress-bar
                          [value]="task.progress.overall ?? 0.2"
                          mode="determinate"
                          class="!my-auto h-1 rounded-sm [--mat-progress-bar-active-indicator-color:theme(colors.red.500)]"
                        ></mat-progress-bar>
                        <mat-icon class="h-4 w-4 text-base text-red-500"> error </mat-icon>
                      }
                    </div>
                    <!-- <button
                    *ngIf="task.status === 'error'"
                    mat-icon-button
                    class="ml-auto text-red-500 hover:bg-red-50"
                    (click)="retryUpload(task)"
                    >
                    <mat-icon>refresh</mat-icon>
                  </button> -->
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    }
  `,
  styleUrls: ['./upload-progress-popup.component.css'],
  animations: [
    fadeInUpOnEnterAnimation({ duration: 300 }),
    fadeOutDownOnLeaveAnimation({ duration: 200 }),
    fadeInOnEnterAnimation({ duration: 200 }),
    fadeOutOnLeaveAnimation({ duration: 150 }),
  ],
})
export class UploadProgressPopupComponent {
  private uploadService = inject(UploadProgressService);

  // Use signals for reactive state
  tasks = this.uploadService.uploadTasks;
  hasActiveUploads = this.uploadService.hasActiveUploads;

  isMinimized = signal(false);

  // Computed for tasks count display
  tasksCount = computed(() => this.tasks().length);

  // Auto-expand when new uploads start
  constructor() {
    let previousTasksCount = 0;

    // Use effect to watch for changes in task count
    effect(() => {
      const currentCount = this.tasks().length;

      // If new tasks were added and we're minimized, expand
      if (currentCount > previousTasksCount && this.isMinimized()) {
        this.isMinimized.set(false);
      }

      previousTasksCount = currentCount;
    });
  }

  toggleMinimize(): void {
    this.isMinimized.set(!this.isMinimized());
  }

  trackByTaskId(index: number, task: UploadTask): string {
    return task.id;
  }

  retryUpload(task: UploadTask): void {
    // This would trigger a retry mechanism
    // For now, just remove the failed task
    this.uploadService.removeUploadTask(task.id);
  }
}
